import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { employeesAPI, tasksAPI, projectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// ── Helpers ──────────────────────────────────────────────────────────────────
const today = new Date().toISOString().split('T')[0];

const fmt = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const diffDays = (a, b) => {
  if (!a || !b) return null;
  return Math.round((new Date(b) - new Date(a)) / 86400000);
};

const Avatar = ({ name, size = 36 }) => {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.33, fontWeight: '700', flexShrink: 0,
    }}>{initials}</div>
  );
};

const STATUS_MAP = {
  2: { label: 'In Progress', color: '#3b82f6', bg: '#eff6ff' },
  3: { label: 'On Hold',     color: '#f59e0b', bg: '#fffbeb' },
  6: { label: 'Not Started', color: '#64748b', bg: '#f8fafc' },
};

const PRIORITY_MAP = {
  Critical: { color: '#dc2626', bg: '#fef2f2' },
  High:     { color: '#ea580c', bg: '#fff7ed' },
  Medium:   { color: '#d97706', bg: '#fffbeb' },
  Low:      { color: '#16a34a', bg: '#f0fdf4' },
};

// ── Main Component ────────────────────────────────────────────────────────────
// ── Allocation Check Panel ─────────────────────────────────────────────────
const AllocCheckPanel = ({ check, checking }) => {
  if (checking) return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: '14px' }}>⏳</span>
      <span style={{ fontSize: '13px', color: '#64748b' }}>Checking capacity…</span>
    </div>
  );
  if (!check) return null;

  const { total_allocation, existing_allocation, is_overallocated, conflicting_tasks } = check;
  const isTight = !is_overallocated && total_allocation >= 80;
  const bg    = is_overallocated ? '#fef2f2' : isTight ? '#fffbeb' : '#f0fdf4';
  const border= is_overallocated ? '#fca5a5' : isTight ? '#fde68a' : '#bbf7d0';
  const icon  = is_overallocated ? '🔴' : isTight ? '🟡' : '🟢';
  const title = is_overallocated
    ? `Over-allocated — ${total_allocation}% total (${total_allocation - 100}% over limit)`
    : isTight
    ? `Tight — ${total_allocation}% total capacity used in this window`
    : `Clear — ${total_allocation}% total, ${100 - total_allocation}% headroom`;
  const sub = existing_allocation > 0
    ? `${existing_allocation}% already committed from ${conflicting_tasks.length} other task${conflicting_tasks.length !== 1 ? 's' : ''} in this window`
    : 'No other tasks in this date window';

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: '10px', padding: '12px 14px', marginTop: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: conflicting_tasks.length > 0 ? '10px' : '0' }}>
        <span style={{ fontSize: '16px' }}>{icon}</span>
        <div>
          <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e293b' }}>{title}</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>{sub}</div>
        </div>
        {/* Mini bar */}
        <div style={{ marginLeft: 'auto', minWidth: '80px' }}>
          <div style={{ height: '6px', background: 'rgba(0,0,0,0.08)', borderRadius: '99px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(total_allocation, 100)}%`, background: is_overallocated ? '#ef4444' : isTight ? '#f59e0b' : '#16a34a', borderRadius: '99px' }} />
          </div>
          <div style={{ fontSize: '10px', fontWeight: '700', color: is_overallocated ? '#ef4444' : isTight ? '#d97706' : '#16a34a', textAlign: 'right', marginTop: '2px' }}>{total_allocation}%</div>
        </div>
      </div>
      {conflicting_tasks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {conflicting_tasks.map(t => (
            <div key={t.task_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.6)', borderRadius: '6px', padding: '5px 8px' }}>
              <div>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{t.task_name}</span>
                <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '6px' }}>{t.project_name}</span>
              </div>
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', flexShrink: 0, marginLeft: '8px' }}>{t.allocation_percent}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DynamicAllocation = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { can, user } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [queue, setQueue] = useState([]);
  const [empInfo, setEmpInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orderSaved, setOrderSaved] = useState(false);

  // Tab navigation
  const [activeTab, setActiveTab] = useState('queue'); // 'queue' | 'schedule'
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());

  // Schedule overrides: { 'YYYY-MM-DD': { task_id: alloc_pct, ... } }
  const [scheduleOverrides, setScheduleOverrides] = useState({});
  const [_scheduleLoading, setScheduleLoading] = useState(false);
  // Popover state: { date, tasks: [{task_id,task_name,color,alloc}], inputs: {task_id: pct} }
  const [schedulePopover, setSchedulePopover] = useState(null);

  // Hold/resume effective-date modal: { task, goingOnHold, date }
  const [holdModal, setHoldModal] = useState(null);

  // New task modal
  const [showTaskModal, setShowTaskModal] = useState(false);

  // Inline reason editing: { taskId, reason }
  const [editingReason, setEditingReason] = useState(null);

  // Edit panel state
  const [editPanel, setEditPanel] = useState(null); // task object being edited
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editSaved, setEditSaved] = useState(false);

  const openEditPanel = (task) => {
    setEditPanel(task);
    setEditSaved(false);
    setEditForm({
      status_id: task.status_id,
      priority: task.priority || 'Medium',
      allocation_percent: task.allocation_percent || 100,
      progress: task.progress || 0,
      effort_days: task.effort_days || '',
      actual_start: task.actual_start || '',
      actual_end: task.actual_end || '',
      forecast_start: task.forecast_start || '',
      forecast_end: task.forecast_end || '',
    });
  };

  const closeEditPanel = () => {
    setEditPanel(null);
    setEditSaved(false);
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      // Merge editable fields with existing task data for the full PUT
      await tasksAPI.update(editPanel.task_id, {
        task_name: editPanel.task_name,
        description: editPanel.description,
        parent_task_id: editPanel.parent_task_id,
        assigned_to: editPanel.assigned_to || selectedEmp,
        planned_start: editPanel.planned_start,
        planned_end: editPanel.planned_end,
        actual_start: editForm.actual_start || null,
        actual_end: editForm.actual_end || null,
        effort_days: editForm.effort_days || null,
        progress: editForm.progress,
        status_id: Number(editForm.status_id),
        priority: editForm.priority,
        allocation_percent: editForm.allocation_percent,
        is_unplanned: editPanel.is_unplanned,
        unplanned_reason: editPanel.unplanned_reason,
        forecast_start: editForm.forecast_start || null,
        forecast_end: editForm.forecast_end || null,
        changed_by_id: user?.user_id,
        changed_by_name: user?.name,
      });
      // Update queue state locally
      setQueue(prev => prev.map(t =>
        t.task_id === editPanel.task_id
          ? {
              ...t,
              status_id: Number(editForm.status_id),
              status_name: STATUS_MAP[Number(editForm.status_id)]?.label || t.status_name,
              priority: editForm.priority,
              allocation_percent: Number(editForm.allocation_percent),
              progress: Number(editForm.progress),
              effort_days: editForm.effort_days || null,
              actual_start: editForm.actual_start || null,
              actual_end: editForm.actual_end || null,
              forecast_start: editForm.forecast_start || null,
              forecast_end: editForm.forecast_end || null,
            }
          : t
      ));
      setEditSaved(true);
      setTimeout(() => closeEditPanel(), 1200);
    } catch (err) {
      alert('Error saving task');
    } finally {
      setEditSaving(false);
    }
  };
  const [projects, setProjects] = useState([]);
  const [taskForm, setTaskForm] = useState({
    task_name: '', project_id: '', description: '',
    planned_start: today, planned_end: '', effort_days: '',
    priority: 'Medium', allocation_percent: 100,
    is_unplanned: true, unplanned_reason: '',
    status_id: 6,
  });
  const [taskFormError, setTaskFormError] = useState('');
  const [taskSaving, setTaskSaving] = useState(false);

  // ── Allocation check state (shared by modal + edit panel) ──────────────────
  const [allocCheck, setAllocCheck] = useState(null);   // null | { total, existing, overallocated, tasks }
  const [allocChecking, setAllocChecking] = useState(false);
  const allocCheckTimer = React.useRef(null);

  const runAllocCheck = useCallback((empId, plannedStart, plannedEnd, allocPct, excludeTaskId = null) => {
    if (!empId || !plannedStart || !plannedEnd || !allocPct) { setAllocCheck(null); return; }
    if (allocCheckTimer.current) clearTimeout(allocCheckTimer.current);
    allocCheckTimer.current = setTimeout(async () => {
      setAllocChecking(true);
      try {
        const params = { planned_start: plannedStart, planned_end: plannedEnd, allocation_percent: allocPct };
        if (excludeTaskId) params.exclude_task_id = excludeTaskId;
        const res = await employeesAPI.checkAllocation(empId, params);
        setAllocCheck(res.data);
      } catch (err) {
        setAllocCheck(null);
      } finally {
        setAllocChecking(false);
      }
    }, 600); // debounce 600ms — reduces DB hits during rapid field editing
  }, []);

  // Load employees on mount
  useEffect(() => {
    employeesAPI.getAll().then(r => setEmployees(r.data)).catch(console.error);
    projectsAPI.getAll().then(r => setProjects(r.data)).catch(console.error);
  }, []);

  // Allocation check — fires when modal date/alloc fields change
  useEffect(() => {
    if (!showTaskModal) { setAllocCheck(null); return; }
    runAllocCheck(selectedEmp, taskForm.planned_start, taskForm.planned_end, taskForm.allocation_percent);
  }, [showTaskModal, taskForm.planned_start, taskForm.planned_end, taskForm.allocation_percent, selectedEmp, runAllocCheck]);

  // Allocation check — fires when edit panel date/alloc fields change
  useEffect(() => {
    if (!editPanel) { setAllocCheck(null); return; }
    runAllocCheck(selectedEmp, editPanel.planned_start, editPanel.planned_end, editForm.allocation_percent, editPanel.task_id);
  }, [editPanel, editForm.allocation_percent, selectedEmp, runAllocCheck]);

  // Lock body scroll completely while schedule popover is open.
  // Saves and restores the current scrollY so the page doesn't jump.
  useEffect(() => {
    if (!schedulePopover) return;
    const scrollY = window.scrollY;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      window.scrollTo(0, scrollY);
    };
  }, [schedulePopover]);

  const loadSchedule = useCallback(async (empId, tasks) => {
    if (!empId || !tasks.length) return;
    const starts = tasks.map(t => t.actual_start || t.planned_start).filter(Boolean);
    const ends   = tasks.map(t => t.planned_end).filter(Boolean);
    if (!starts.length && !ends.length) return;
    const from = starts.length ? starts.reduce((a, b) => a < b ? a : b) : today;
    const to   = ends.length   ? ends.reduce((a, b) => a > b ? a : b)   : today;
    setScheduleLoading(true);
    try {
      const res = await employeesAPI.getSchedule(empId, from, to);
      const map = {};
      res.data.forEach(row => {
        if (!map[row.schedule_date]) map[row.schedule_date] = {};
        map[row.schedule_date][row.task_id] = Math.round(row.allocation_percent);
      });
      setScheduleOverrides(map);
    } catch (err) {
      console.error('Error loading schedule:', err);
    } finally {
      setScheduleLoading(false);
    }
  }, []);

  const loadQueue = useCallback(async (empId) => {
    setLoading(true);
    setOrderSaved(false);
    try {
      const res = await tasksAPI.getQueue(empId);
      setEmpInfo(res.data.employee);
      const tasks = res.data.tasks.map((t, i) => ({ ...t, queue_position: i + 1 }));
      setQueue(tasks);
      loadSchedule(empId, tasks);
    } catch (err) {
      console.error('Error loading queue:', err);
    } finally {
      setLoading(false);
    }
  }, [loadSchedule]);

  // Load queue when employeeId param changes
  useEffect(() => {
    if (employeeId) {
      setSelectedEmp(Number(employeeId));
      loadQueue(employeeId);
    }
  }, [employeeId, loadQueue]);

  const handleSelectEmployee = (empId) => {
    navigate(`/allocation/${empId}`);
  };

  // Move task up/down in queue
  const moveTask = (index, direction) => {
    const newQueue = [...queue];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newQueue.length) return;
    [newQueue[index], newQueue[swapIndex]] = [newQueue[swapIndex], newQueue[index]];
    newQueue.forEach((t, i) => { t.queue_position = i + 1; });
    setQueue(newQueue);
  };

  // Toggle on hold
  // Open the effective-date modal instead of toggling immediately, so the user
  // chooses the date the hold/resume takes effect. Past days are untouched.
  const toggleHold = (task) => {
    const goingOnHold = task.status_id !== 3;
    setHoldModal({ task, goingOnHold, date: todayStr });
  };

  // Apply hold/resume from the chosen effective date and update local hold
  // intervals so the grid recomputes per-day held state without a refetch.
  const confirmHold = async () => {
    if (!holdModal) return;
    const { task, goingOnHold, date } = holdModal;
    try {
      await tasksAPI.toggleHold(task.task_id, goingOnHold, date, user?.user_id, user?.name);
      setQueue(prev => prev.map(t => {
        if (t.task_id !== task.task_id) return t;
        const periods = Array.isArray(t.hold_periods) ? t.hold_periods.map(p => ({ ...p })) : [];
        if (goingOnHold) {
          // Open a new interval if none currently open; else pull its start earlier.
          const open = periods.find(p => p.hold_to === null || p.hold_to === undefined);
          if (open) { open.hold_from = open.hold_from < date ? open.hold_from : date; }
          else { periods.push({ hold_from: date, hold_to: null }); }
        } else {
          // Close the latest open interval at the chosen date (or drop it if the
          // resume date is on/before its start, mirroring the backend).
          const open = [...periods].reverse().find(p => p.hold_to === null || p.hold_to === undefined);
          if (open) {
            if (date <= open.hold_from) {
              const idx = periods.indexOf(open);
              periods.splice(idx, 1);
            } else {
              open.hold_to = date;
            }
          }
        }
        return {
          ...t,
          status_id: goingOnHold ? 3 : 2,
          status_name: goingOnHold ? 'On Hold' : 'In Progress',
          hold_periods: periods,
        };
      }));
      setHoldModal(null);
    } catch (err) {
      alert('Error updating task status');
    }
  };

  // Toggle unplanned flag — if turning ON, open reason editor; if turning OFF, clear immediately
  const toggleUnplanned = async (task) => {
    if (task.is_unplanned) {
      // Turn off immediately
      try {
        await tasksAPI.setUnplanned(task.task_id, false, null, user?.user_id, user?.name);
        setQueue(prev => prev.map(t =>
          t.task_id === task.task_id ? { ...t, is_unplanned: 0, unplanned_reason: null } : t
        ));
      } catch (err) {
        alert('Error updating task');
      }
    } else {
      // Open inline reason editor
      setEditingReason({ taskId: task.task_id, reason: task.unplanned_reason || '' });
    }
  };

  const saveUnplannedReason = async () => {
    if (!editingReason) return;
    try {
      await tasksAPI.setUnplanned(editingReason.taskId, true, editingReason.reason, user?.user_id, user?.name);
      setQueue(prev => prev.map(t =>
        t.task_id === editingReason.taskId
          ? { ...t, is_unplanned: 1, unplanned_reason: editingReason.reason }
          : t
      ));
      setEditingReason(null);
    } catch (err) {
      alert('Error saving reason');
    }
  };

  // Save queue + calculate forecasts
  const saveQueue = async () => {
    setSaving(true);
    try {
      const payload = queue.map(t => ({
        task_id: t.task_id,
        project_id: t.project_id,
        queue_position: t.queue_position,
        status_id: t.status_id,
        queue_notes: t.queue_notes || null,
      }));
      await tasksAPI.reorderQueue(selectedEmp, { tasks: payload });
      setOrderSaved(true);
      setTimeout(() => setOrderSaved(false), 3000);
    } catch (err) {
      alert('Error saving queue order');
    } finally {
      setSaving(false);
    }
  };

  // Create new task
  const handleCreateTask = async (e) => {
    e.preventDefault();
    setTaskFormError('');
    if (!taskForm.task_name.trim()) { setTaskFormError('Task name is required.'); return; }
    if (!taskForm.project_id) { setTaskFormError('Please select a project.'); return; }
    if (!selectedEmp) { setTaskFormError('No employee selected.'); return; }
    setTaskSaving(true);
    try {
      const payload = {
        ...taskForm,
        assigned_to: parseInt(selectedEmp, 10),
        status_id: Number(taskForm.status_id),
        allocation_percent: Number(taskForm.allocation_percent) || 100,
        effort_days: taskForm.effort_days ? Number(taskForm.effort_days) : null,
      };
      console.log('Creating task with payload:', payload); // diagnostic
      await tasksAPI.create(taskForm.project_id, payload);
      setShowTaskModal(false);
      setTaskForm({
        task_name: '', project_id: '', description: '',
        planned_start: today, planned_end: '', effort_days: '',
        priority: 'Medium', allocation_percent: 100,
        is_unplanned: true, unplanned_reason: '', status_id: 6,
      });
      await loadQueue(selectedEmp);
    } catch (err) {
      setTaskFormError('Error creating task.');
    } finally {
      setTaskSaving(false);
    }
  };

  // ── Derived stats (week-aware capacity model) ─────────────────────────────
  const todayStr = new Date().toISOString().split('T')[0];

  const getTaskDateStatus = (task) => {
    if (task.status_id === 3) return 'on_hold';
    const start = task.forecast_start || task.actual_start || task.planned_start;
    const end = task.planned_end;
    if (!start && !end) return 'active';
    if (end && end < todayStr && !task.actual_end) return 'slipping';
    if (start && start > todayStr) return 'upcoming';
    return 'active';
  };

  // This week's load: sum allocations of tasks active or slipping right now
  const activeTasks   = queue.filter(t => getTaskDateStatus(t) === 'active');
  const slippingTasks = queue.filter(t => getTaskDateStatus(t) === 'slipping');
  const upcomingTasks = queue.filter(t => getTaskDateStatus(t) === 'upcoming');
  const todayLoad     = [...activeTasks, ...slippingTasks].reduce((s, t) => s + (t.allocation_percent || 0), 0);

  const unplannedCount = queue.filter(t => t.is_unplanned).length;
  const onHoldCount    = queue.filter(t => t.status_id === 3).length;
  const allocColor     = todayLoad > 100 ? '#ef4444' : todayLoad >= 80 ? '#f59e0b' : '#16a34a';

  // ── Schedule computation ──────────────────────────────────────────────────
  // A palette of distinct colours per task (by queue position)
  const TASK_COLORS = [
    '#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444',
    '#06b6d4','#ec4899','#84cc16','#f97316','#6366f1',
  ];

  // Render-scope colour map: task_id → colour, ordered by queue (matches legend).
  const colorMap = {};
  queue.forEach((t, i) => { colorMap[t.task_id] = TASK_COLORS[i % TASK_COLORS.length]; });

  // Hex colour + alpha → rgba string, for low-opacity per-row cell tints.
  const hexA = (hex, alpha) => {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const isWorkday = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    const dow = d.getDay();
    return dow !== 0 && dow !== 6;
  };

  const addDay = (dateStr, n = 1) => {
    const d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + n);
    return d.toISOString().split('T')[0];
  };

  const getMondayOf = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    const dow = d.getDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    d.setDate(d.getDate() + diff);
    return d.toISOString().split('T')[0];
  };

  // Returns tasks active on a given date (not completed, not on hold, date in range)
  const getTasksOnDay = (dateStr) => {
    return queue.filter(t => {
      if (t.status_id === 3 || t.status_id === 5) return false;
      const start = t.forecast_start || t.actual_start || t.planned_start;
      const end   = t.planned_end;
      if (!start && !end) return true;
      if (start && dateStr < start) return false;
      // Slipping tasks (past planned_end, no actual_end): show from today,
      // capped by forecast_end if set and valid — mirrors ResourceView Rule 4
      if (end && dateStr > end) {
        const isSlipping = end < todayStr && !t.actual_end;
        if (isSlipping) {
          if (dateStr < todayStr) return false;
          if (t.forecast_end && t.forecast_end >= todayStr) return dateStr <= t.forecast_end;
          return true; // no valid forecast → show indefinitely
        }
        return false;
      }
      // Active tasks: use forecast_end as effective end if set and beyond planned_end
      const effectiveEnd = (t.forecast_end && t.forecast_end >= end) ? t.forecast_end : end;
      return dateStr <= effectiveEnd;
    });
  };

  // Frontend mirror of the backend hold-interval check. A day is held if it falls
  // in any [hold_from, hold_to) interval (hold_to null = open-ended).
  const isHeldOn = (task, dateStr) => {
    const periods = task.hold_periods;
    if (!Array.isArray(periods) || periods.length === 0) {
      // No interval data — we cannot know WHEN the hold started, so date-aware
      // behaviour is not possible. Fall back to the original blanket rule: an
      // on-hold task is hidden on every day. Guessing "held from today" would
      // wrongly re-expose the task on all past days.
      return task.status_id === 3;
    }
    for (const p of periods) {
      const to = p.hold_to == null ? null : p.hold_to;
      if (p.hold_from <= dateStr && (to === null || dateStr < to)) return true;
    }
    return false;
  };

  // Grid membership resolver. Mirrors getTasksOnDay's DATE logic (which is kept
  // byte-identical to ResourceView for the locked slipping/forecast rule), but
  // replaces the blanket status_id===3 exclusion with a per-day held check, so a
  // task that is on hold now still appears on its past, non-held days and only the
  // held date ranges are dropped. Status 5 (cancelled) is still excluded outright.
  // Current-week days before today are resolved as "today" for membership only, so
  // the current week's already-passed weekdays still show their tasks.
  const gridTasksOnDay = (dateStr) => {
    const thisWeekMonday = getMondayOf(todayStr);
    // Membership date: clamp pre-today current-week days to today (display only).
    const refDate = (dateStr >= thisWeekMonday && dateStr < todayStr) ? todayStr : dateStr;
    return queue.filter(t => {
      if (t.status_id === 5) return false;
      if (isHeldOn(t, dateStr)) return false;            // held on the REAL date → drop
      const start = t.forecast_start || t.actual_start || t.planned_start;
      const end   = t.planned_end;
      if (!start && !end) return true;
      if (start && refDate < start) return false;
      if (end && refDate > end) {
        const isSlipping = end < todayStr && !t.actual_end;
        if (isSlipping) {
          if (refDate < todayStr) return false;
          if (t.forecast_end && t.forecast_end >= todayStr) return refDate <= t.forecast_end;
          return true;
        }
        return false;
      }
      const effectiveEnd = (t.forecast_end && t.forecast_end >= end) ? t.forecast_end : end;
      return refDate <= effectiveEnd;
    });
  };

  // Build schedule: array of week objects each with array of day objects
  const buildSchedule = () => {
    const activeTasks = queue.filter(t => t.status_id !== 5);
    if (activeTasks.length === 0) return [];

    const starts = activeTasks.map(t => t.forecast_start || t.actual_start || t.planned_start).filter(Boolean);
    // For range calc: use forecast_end as effective end when set and valid, else planned_end.
    // Slipping tasks with no forecast fall back to todayStr so they don't vanish from the range.
    const ends = activeTasks.map(t => {
      const end = t.planned_end;
      if (!end) return null;
      const isSlipping = end < todayStr && !t.actual_end && t.status_id !== 5;
      if (isSlipping) {
        return (t.forecast_end && t.forecast_end >= todayStr) ? t.forecast_end : todayStr;
      }
      // Active/upcoming: use forecast_end if it extends beyond planned_end
      return (t.forecast_end && t.forecast_end >= end) ? t.forecast_end : end;
    }).filter(Boolean);
    const thisWeekMonday = getMondayOf(todayStr);
    const earliestStart  = getMondayOf(starts.length ? starts.reduce((a, b) => a < b ? a : b) : todayStr);
    // Never show weeks in the past — clamp to current week's Monday
    const rangeStart = earliestStart > thisWeekMonday ? earliestStart : thisWeekMonday;
    const maxEnd     = addDay(getMondayOf(todayStr), 12 * 7);
    // Always show at least the current week PLUS the next full week (Mon–Sun ×2),
    // so there is always somewhere to plan forward — even on a Fri/Sat/Sun when the
    // current week has no workdays left, or when tasks are slipping with no end date.
    // Real task ends extend the window further (capped at maxEnd).
    const minEnd     = addDay(getMondayOf(todayStr), 13);
    const latestEnd  = ends.length
      ? [ends.reduce((a, b) => a > b ? a : b), maxEnd].reduce((a, b) => a < b ? a : b)
      : minEnd;
    const rangeEnd   = latestEnd > minEnd ? latestEnd : minEnd;

    const colorMap = {};
    queue.forEach((t, i) => { colorMap[t.task_id] = TASK_COLORS[i % TASK_COLORS.length]; });

    const weeks = [];
    let cursor = rangeStart;

    while (cursor <= rangeEnd) {
      const weekStart = cursor;
      const days = [];

      for (let d = 0; d < 7; d++) {
        const dayStr = addDay(cursor, d);
        if (dayStr > rangeEnd) break;
        if (!isWorkday(dayStr)) continue;

        const dayTasks     = gridTasksOnDay(dayStr);
        const dayOverrides = scheduleOverrides[dayStr] || null;
        const hasOverride  = !!dayOverrides;

        const resolvedTasks = dayTasks
          .filter(t => {
            // If a saved override exists for this day, only show tasks explicitly saved with > 0.
            // Tasks absent from the override were not allocated that day.
            if (dayOverrides) return dayOverrides[t.task_id] !== undefined && dayOverrides[t.task_id] > 0;
            return true;
          })
          .map(t => {
            const alloc = dayOverrides && dayOverrides[t.task_id] !== undefined
              ? Math.round(dayOverrides[t.task_id])
              : (t.allocation_percent || 0);
            return { ...t, effectiveAlloc: alloc };
          });

        const totalAlloc = resolvedTasks.reduce((s, t) => s + t.effectiveAlloc, 0);

        days.push({
          date: dayStr,
          hasOverride,
          tasks: resolvedTasks.map(t => ({
            task_id:      t.task_id,
            task_name:    t.task_name,
            project:      t.project_name,
            alloc:        t.effectiveAlloc,
            defaultAlloc: t.allocation_percent || 0,
            color:        colorMap[t.task_id],
            status_id:    t.status_id,
            isSlipping:   getTaskDateStatus(t) === 'slipping',
          })),
          totalAlloc,
          isToday: dayStr === todayStr,
          isPast:  dayStr < todayStr,
        });
      }

      if (days.length > 0) {
        const weekAllTasks = [...new Set(days.flatMap(d => d.tasks.map(t => t.task_id)))]
          .map(id => queue.find(t => t.task_id === id))
          .filter(Boolean);
        weeks.push({ weekStart, days, tasks: weekAllTasks, colorMap });
      }
      cursor = addDay(cursor, 7);
    }
    return weeks;
  };

  const schedule = buildSchedule();

  const toggleWeek = (weekStart) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      next.has(weekStart) ? next.delete(weekStart) : next.add(weekStart);
      return next;
    });
  };

  const openDayPopover = (day) => {
    // Always show ALL tasks active on this day in the popover, not just the saved ones.
    // This lets users add tasks to a day that already has a partial override saved.
    const allDayTasks = getTasksOnDay(day.date);
    const savedOverride = scheduleOverrides[day.date] || null;
    const colorMap = {};
    queue.forEach((t, i) => { colorMap[t.task_id] = TASK_COLORS[i % TASK_COLORS.length]; });
    const popoverTasks = allDayTasks.map(t => ({
      task_id:      t.task_id,
      task_name:    t.task_name,
      project:      t.project_name,
      defaultAlloc: t.allocation_percent || 0,
      color:        colorMap[t.task_id],
    }));
    if (popoverTasks.length === 0) return;
    setSchedulePopover({
      date: day.date,
      // Saved override present: seed saved tasks with their value, rest with 0.
      // No override: seed all tasks with their default allocation.
      inputs: Object.fromEntries(popoverTasks.map(t => [
        t.task_id,
        savedOverride
          ? (savedOverride[t.task_id] !== undefined ? savedOverride[t.task_id] : 0)
          : t.defaultAlloc,
      ])),
      tasks: popoverTasks,
      saving: false,
      saved: false,
    });
  };

  const saveScheduleDay = async () => {
    if (!schedulePopover || !selectedEmp) return;
    setSchedulePopover(p => ({ ...p, saving: true }));
    try {
      const allocations = Object.entries(schedulePopover.inputs).map(([task_id, allocation_percent]) => ({
        task_id: parseInt(task_id),
        allocation_percent: parseFloat(allocation_percent) || 0,
      }));
      await employeesAPI.saveSchedule(selectedEmp, {
        schedule_date: schedulePopover.date,
        allocations,
      });
      // Update local overrides immediately
      setScheduleOverrides(prev => ({
        ...prev,
        [schedulePopover.date]: Object.fromEntries(
          allocations.map(a => [a.task_id, Math.round(a.allocation_percent)])
        ),
      }));
      setSchedulePopover(p => ({ ...p, saving: false, saved: true }));
      setTimeout(() => setSchedulePopover(null), 800);
    } catch (err) {
      console.error('Error saving schedule:', err);
      setSchedulePopover(p => ({ ...p, saving: false }));
    }
  };

  // ── Week-grid editing ───────────────────────────────────────────────────────
  // Resolve the displayed % for a task on a day: saved override wins, else the
  // task's default allocation_percent. Identical resolution to openDayPopover.
  const gridCellValue = (dateStr, task) => {
    const ov = scheduleOverrides[dateStr];
    if (ov && ov[task.task_id] !== undefined) return ov[task.task_id];
    return task.allocation_percent || 0;
  };

  // Write a single cell into local override state (no API call yet — batched on save).
  const setGridCell = (dateStr, taskId, value) => {
    const v = Math.min(100, Math.max(0, parseInt(value) || 0));
    setScheduleOverrides(prev => {
      // Seed the day from current resolved values so untouched cells keep their
      // defaults when we later save the whole day.
      const dayTasks = gridTasksOnDay(dateStr);
      const base = {};
      dayTasks.forEach(t => { base[t.task_id] = gridCellValue(dateStr, t); });
      return { ...prev, [dateStr]: { ...base, ...(prev[dateStr] || {}), [taskId]: v } };
    });
  };

  // Copy one weekday's values across all editable weekdays in the same week.
  const copyDayAcrossWeek = (week, sourceDateStr) => {
    const srcTasks = gridTasksOnDay(sourceDateStr);
    const srcVals = {};
    srcTasks.forEach(t => { srcVals[t.task_id] = gridCellValue(sourceDateStr, t); });
    setScheduleOverrides(prev => {
      const next = { ...prev };
      week.days.forEach(day => {
        const dayTasks = gridTasksOnDay(day.date);
        const merged = { ...(next[day.date] || {}) };
        dayTasks.forEach(t => {
          merged[t.task_id] = srcVals[t.task_id] !== undefined
            ? srcVals[t.task_id]
            : gridCellValue(day.date, t);
        });
        next[day.date] = merged;
      });
      return next;
    });
  };

  // Save every editable weekday in a week — one saveSchedule call per day,
  // reusing the exact same contract as saveScheduleDay.
  const [savingWeek, setSavingWeek] = useState(null);   // weekStart currently saving
  const [savedWeek, setSavedWeek]   = useState(null);   // weekStart just saved (for ✓)
  const saveScheduleWeek = async (week) => {
    if (!selectedEmp) return;
    setSavingWeek(week.weekStart);
    try {
      for (const day of week.days) {
        const dayTasks = gridTasksOnDay(day.date);
        if (dayTasks.length === 0) continue;
        const allocations = dayTasks.map(t => ({
          task_id: t.task_id,
          allocation_percent: gridCellValue(day.date, t),
        }));
        await employeesAPI.saveSchedule(selectedEmp, {
          schedule_date: day.date,
          allocations,
        });
        // Persist into local overrides so the grid reflects the saved state.
        setScheduleOverrides(prev => ({
          ...prev,
          [day.date]: Object.fromEntries(allocations.map(a => [a.task_id, Math.round(a.allocation_percent)])),
        }));
      }
      setSavingWeek(null);
      setSavedWeek(week.weekStart);
      setTimeout(() => setSavedWeek(null), 1500);
    } catch (err) {
      console.error('Error saving week schedule:', err);
      setSavingWeek(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'inherit' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '28px' }}>
        <h2 style={{ fontSize: '26px', fontWeight: '800', color: '#1e293b', margin: 0 }}>
          ⚡ Dynamic Resource Allocation
        </h2>
        <p style={{ color: '#64748b', marginTop: '4px', fontSize: '14px' }}>
          Manage task queues, flag unplanned work, rebalance priorities and calculate forecast impact
        </p>
      </div>

      {/* Employee selector */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 className="card-title">Select Resource</h3>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {employees.map(emp => (
            <button
              key={emp.employee_id}
              onClick={() => handleSelectEmployee(emp.employee_id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 16px', borderRadius: '10px', cursor: 'pointer',
                border: `2px solid ${selectedEmp === emp.employee_id ? '#3b82f6' : '#e2e8f0'}`,
                background: selectedEmp === emp.employee_id ? '#eff6ff' : '#fff',
                fontWeight: selectedEmp === emp.employee_id ? '700' : '500',
                color: selectedEmp === emp.employee_id ? '#1d4ed8' : '#374151',
                fontSize: '13px', transition: 'all 0.15s',
              }}
            >
              <Avatar name={emp.employee_name} size={28} />
              {emp.employee_name}
              {emp.role && <span style={{ color: '#94a3b8', fontWeight: '400' }}>· {emp.role}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Queue workspace */}
      {selectedEmp && (
        <>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>Loading queue…</div>
          ) : (
            <>
              {/* Resource header bar */}
              {empInfo && (
                <div style={{
                  background: '#fff', borderRadius: '12px', padding: '20px',
                  border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  marginBottom: '20px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                    {/* Employee info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Avatar name={empInfo.employee_name} size={44} />
                      <div>
                        <div style={{ fontWeight: '800', fontSize: '16px', color: '#1e293b' }}>{empInfo.employee_name}</div>
                        <div style={{ fontSize: '12px', color: '#94a3b8' }}>{empInfo.role}{empInfo.department ? ` · ${empInfo.department}` : ''}</div>
                      </div>
                    </div>
                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {unplannedCount > 0 && (
                        <span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: '700' }}>
                          🚨 {unplannedCount} unplanned
                        </span>
                      )}
                      {onHoldCount > 0 && (
                        <span style={{ background: '#fffbeb', color: '#d97706', borderRadius: '20px', padding: '4px 12px', fontSize: '12px', fontWeight: '700' }}>
                          ⏸ {onHoldCount} on hold
                        </span>
                      )}
                      {can('createTaskInAlloc') && (
                      <button onClick={() => setShowTaskModal(true)} className="btn btn-primary btn-sm"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        ＋ Add Task
                      </button>
                      )}
                    </div>
                  </div>

                  {/* Weekly capacity breakdown */}
                  <div style={{ marginTop: '16px' }}>

                    {/* This week's load bar */}
                    <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>This Week's Load</span>
                        <span style={{ fontSize: '14px', fontWeight: '800', color: allocColor }}>{todayLoad}%</span>
                      </div>
                      <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(todayLoad, 100)}%`, background: allocColor, borderRadius: '99px', transition: 'width 0.3s' }} />
                      </div>
                      {todayLoad > 100 && (
                        <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px', fontWeight: '700' }}>
                          ⚠️ Over-allocated by {todayLoad - 100}% this week
                        </div>
                      )}
                    </div>

                    {/* Stat pills row */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                      <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '8px 14px', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#1d4ed8' }}>{activeTasks.length}</div>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#3b82f6', textTransform: 'uppercase' }}>Active</div>
                      </div>
                      <div style={{ background: slippingTasks.length > 0 ? '#fef2f2' : '#f8fafc', borderRadius: '8px', padding: '8px 14px', border: `1px solid ${slippingTasks.length > 0 ? '#fca5a5' : '#e2e8f0'}`, textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: slippingTasks.length > 0 ? '#dc2626' : '#94a3b8' }}>{slippingTasks.length}</div>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: slippingTasks.length > 0 ? '#ef4444' : '#94a3b8', textTransform: 'uppercase' }}>Slipping</div>
                      </div>
                      <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '8px 14px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: '#15803d' }}>{upcomingTasks.length}</div>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#16a34a', textTransform: 'uppercase' }}>Upcoming</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Slipping tasks banner */}
              {slippingTasks.length > 0 && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fca5a5', borderLeft: '4px solid #ef4444',
                  borderRadius: '10px', padding: '12px 16px', marginBottom: '12px',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <span style={{ fontSize: '20px' }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '14px' }}>
                      {slippingTasks.length} task{slippingTasks.length > 1 ? 's are' : ' is'} past planned end date and still open
                    </div>
                    <div style={{ color: '#7f1d1d', fontSize: '12px', marginTop: '2px' }}>
                      These tasks are consuming {slippingTasks.reduce((s, t) => s + (t.allocation_percent || 0), 0)}% capacity beyond their planned window. Update actual dates or put on hold if not actively worked.
                    </div>
                  </div>
                </div>
              )}

              {/* Unplanned tasks banner */}
              {unplannedCount > 0 && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fca5a5', borderLeft: '4px solid #ef4444',
                  borderRadius: '10px', padding: '12px 16px', marginBottom: '16px',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <span style={{ fontSize: '20px' }}>🚨</span>
                  <div>
                    <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '14px' }}>
                      {unplannedCount} unplanned task{unplannedCount > 1 ? 's' : ''} in this queue
                    </div>
                    <div style={{ color: '#7f1d1d', fontSize: '12px', marginTop: '2px' }}>
                      Review priorities below, put lower-priority tasks on hold if needed, then reorder and click "Save Order".
                    </div>
                  </div>
                </div>
              )}

              {/* Tab bar */}
              <div style={{ display: 'flex', gap: '0', marginBottom: '0', borderBottom: '2px solid #e2e8f0' }}>
                {[
                  { id: 'queue',    label: '📋 Task Queue' },
                  { id: 'schedule', label: '📅 Schedule' },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                    padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
                    fontSize: '13px', fontWeight: '700',
                    color: activeTab === tab.id ? '#2563eb' : '#64748b',
                    borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
                    marginBottom: '-2px', transition: 'color 0.15s',
                  }}>{tab.label}</button>
                ))}
              </div>

              {/* Queue table */}
              {activeTab === 'queue' && <div className="card" style={{ marginBottom: '24px', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <div className="card-header">
                  <h3 className="card-title">📋 Task Queue</h3>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>Drag order = priority order</span>
                    <button
                      onClick={saveQueue}
                      disabled={saving || queue.length === 0}
                      className="btn btn-primary btn-sm"
                    >
                      {saving ? '⏳ Saving…' : '💾 Save Order'}
                    </button>
                  </div>
                </div>

                {queue.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                    No active tasks in queue. Add a task using the button above.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                          {['#', 'Task', 'Project / Customer', 'Priority', 'Dates', 'Progress', 'Alloc%', 'Flags', 'Actions'].map(h => (
                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {queue.map((task, index) => {
                          const isOnHold = task.status_id === 3;
                          const statusCfg = STATUS_MAP[task.status_id] || STATUS_MAP[6];
                          const priCfg = PRIORITY_MAP[task.priority] || PRIORITY_MAP.Medium;
                          const dateStatus = getTaskDateStatus(task);

                          const dateStatusCfg = {
                            active:   { label: '● Active today',  color: '#3b82f6', bg: '#eff6ff', border: '#3b82f6' },
                            slipping: { label: '⚠ Slipping',      color: '#ef4444', bg: '#fef2f2', border: '#ef4444' },
                            upcoming: { label: '◷ Upcoming',      color: '#16a34a', bg: '#f0fdf4', border: '#16a34a' },
                            on_hold:  { label: '⏸ On Hold',       color: '#d97706', bg: '#fffbeb', border: '#f59e0b' },
                          }[dateStatus] || { label: '', color: '#94a3b8', bg: '#f8fafc', border: 'transparent' };

                          return (
                            <tr key={task.task_id} style={{
                              borderBottom: '1px solid #f1f5f9',
                              borderLeft: `3px solid ${dateStatusCfg.border}`,
                              background: isOnHold ? '#fffbeb' : dateStatus === 'slipping' ? '#fff8f8' : task.is_unplanned ? '#fff7f7' : '#fff',
                              opacity: isOnHold ? 0.8 : 1,
                              transition: 'background 0.15s',
                            }}>
                              {/* Position */}
                              <td style={{ padding: '12px', fontWeight: '800', color: '#94a3b8', width: '40px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <span style={{ fontSize: '13px', color: '#1e293b' }}>{task.queue_position}</span>
                                  <div style={{ display: 'flex', gap: '2px' }}>
                                    <button onClick={() => moveTask(index, 'up')} disabled={index === 0}
                                      style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: index === 0 ? 'default' : 'pointer', padding: '1px 4px', fontSize: '10px', color: index === 0 ? '#cbd5e1' : '#64748b' }}>▲</button>
                                    <button onClick={() => moveTask(index, 'down')} disabled={index === queue.length - 1}
                                      style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '4px', cursor: index === queue.length - 1 ? 'default' : 'pointer', padding: '1px 4px', fontSize: '10px', color: index === queue.length - 1 ? '#cbd5e1' : '#64748b' }}>▼</button>
                                  </div>
                                </div>
                              </td>

                              {/* Task name */}
                              <td style={{ padding: '12px', minWidth: '160px' }}>
                                <div
                                  onClick={() => openEditPanel(task)}
                                  style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px', cursor: 'pointer' }}
                                  onMouseEnter={e => { e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.textDecoration = 'underline'; }}
                                  onMouseLeave={e => { e.currentTarget.style.color = '#1e293b'; e.currentTarget.style.textDecoration = 'none'; }}
                                >
                                  {task.task_name}
                                </div>
                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                  <span style={{ background: statusCfg.bg, color: statusCfg.color, borderRadius: '6px', padding: '2px 7px', fontSize: '11px', fontWeight: '600' }}>
                                    {statusCfg.label}
                                  </span>
                                  <span style={{ background: dateStatusCfg.bg, color: dateStatusCfg.color, borderRadius: '6px', padding: '2px 7px', fontSize: '11px', fontWeight: '600' }}>
                                    {dateStatusCfg.label}
                                  </span>
                                </div>
                              </td>

                              {/* Project / Customer */}
                              <td style={{ padding: '12px', minWidth: '140px' }}>
                                <Link to={`/projects/${task.project_id}`} style={{ color: '#3b82f6', fontWeight: '600', fontSize: '13px', textDecoration: 'none' }}>
                                  {task.project_name}
                                </Link>
                                {task.customer_name && (
                                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>👤 {task.customer_name}</div>
                                )}
                              </td>

                              {/* Priority */}
                              <td style={{ padding: '12px' }}>
                                <span style={{ background: priCfg.bg, color: priCfg.color, borderRadius: '6px', padding: '3px 8px', fontSize: '11px', fontWeight: '700' }}>
                                  {task.priority || 'Medium'}
                                </span>
                              </td>

                              {/* Dates */}
                              <td style={{ padding: '12px', minWidth: '130px' }}>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                  <span title="Planned">{fmt(task.planned_start)} → {fmt(task.planned_end)}</span>
                                </div>
                                {task.actual_start && (
                                  <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '2px' }}>
                                    ▶ Started {fmt(task.actual_start)}
                                  </div>
                                )}
                                {dateStatus === 'slipping' && (
                                  <div style={{ fontSize: '11px', color: '#ef4444', fontWeight: '700', marginTop: '2px' }}>
                                    {diffDays(task.planned_end, todayStr)}d overdue
                                  </div>
                                )}
                                {dateStatus === 'upcoming' && task.planned_start && (
                                  <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '2px' }}>
                                    starts in {diffDays(todayStr, task.planned_start)}d
                                  </div>
                                )}
                              </td>

                              {/* Progress */}
                              <td style={{ padding: '12px', textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{task.progress || 0}% done</div>
                              </td>

                              {/* Allocation */}
                              <td style={{ padding: '12px', minWidth: '90px' }}>
                                {(() => {
                                  const alloc = task.allocation_percent || 0;
                                  const ds = getTaskDateStatus(task);
                                  const color = ds === 'slipping' ? '#ef4444'
                                    : alloc >= 100 ? '#f59e0b' : '#3b82f6';
                                  return (
                                    <div>
                                      <div style={{ fontWeight: '800', fontSize: '13px', color, marginBottom: '4px' }}>{alloc}%</div>
                                      <div style={{ height: '4px', background: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${Math.min(alloc, 100)}%`, background: color, borderRadius: '99px' }} />
                                      </div>
                                    </div>
                                  );
                                })()}
                              </td>

                              {/* Flags */}
                              <td style={{ padding: '12px', minWidth: '160px' }}>
                                {task.is_unplanned ? (
                                  <div>
                                    <span style={{ background: '#fef2f2', color: '#dc2626', borderRadius: '20px', padding: '2px 8px', fontSize: '10px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                      🚨 Unplanned
                                    </span>
                                    {editingReason && editingReason.taskId === task.task_id ? (
                                      <div style={{ marginTop: '6px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                                        <input
                                          autoFocus
                                          value={editingReason.reason}
                                          onChange={e => setEditingReason(r => ({ ...r, reason: e.target.value }))}
                                          onKeyDown={e => { if (e.key === 'Enter') saveUnplannedReason(); if (e.key === 'Escape') setEditingReason(null); }}
                                          placeholder="Reason…"
                                          style={{ fontSize: '11px', padding: '3px 7px', borderRadius: '6px', border: '1px solid #fca5a5', width: '110px', outline: 'none' }}
                                        />
                                        <button onClick={saveUnplannedReason} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '5px', padding: '3px 7px', fontSize: '11px', cursor: 'pointer', fontWeight: '700' }}>✓</button>
                                        <button onClick={() => setEditingReason(null)} style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '5px', padding: '3px 6px', fontSize: '11px', cursor: 'pointer' }}>✕</button>
                                      </div>
                                    ) : (
                                      <div
                                        onClick={() => setEditingReason({ taskId: task.task_id, reason: task.unplanned_reason || '' })}
                                        style={{ fontSize: '11px', color: task.unplanned_reason ? '#7f1d1d' : '#fca5a5', marginTop: '3px', cursor: 'pointer', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        title={task.unplanned_reason || 'Click to add reason'}
                                      >
                                        {task.unplanned_reason || '+ add reason'}
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                              </td>

                              {/* Actions */}
                              <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  {/* Edit */}
                                  <button
                                    onClick={() => openEditPanel(task)}
                                    title="Edit task"
                                    style={{
                                      width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer',
                                      border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8',
                                      fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#eff6ff'}
                                  >
                                    ✏️
                                  </button>
                                  {/* Hold / Resume */}
                                  <button
                                    onClick={() => toggleHold(task)}
                                    title={isOnHold ? 'Resume task' : 'Put on hold'}
                                    style={{
                                      width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer',
                                      border: `1px solid ${isOnHold ? '#86efac' : '#fde68a'}`,
                                      background: isOnHold ? '#f0fdf4' : '#fffbeb',
                                      color: isOnHold ? '#16a34a' : '#d97706',
                                      fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = isOnHold ? '#dcfce7' : '#fef3c7'}
                                    onMouseLeave={e => e.currentTarget.style.background = isOnHold ? '#f0fdf4' : '#fffbeb'}
                                  >
                                    {isOnHold ? '▶' : '⏸'}
                                  </button>
                                  {/* Flag / Planned */}
                                  <button
                                    onClick={() => toggleUnplanned(task)}
                                    title={task.is_unplanned ? 'Mark as planned' : 'Flag as unplanned'}
                                    style={{
                                      width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer',
                                      border: `1px solid ${task.is_unplanned ? '#cbd5e1' : '#fecaca'}`,
                                      background: task.is_unplanned ? '#f8fafc' : '#fef2f2',
                                      color: task.is_unplanned ? '#64748b' : '#dc2626',
                                      fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = task.is_unplanned ? '#f1f5f9' : '#fee2e2'}
                                    onMouseLeave={e => e.currentTarget.style.background = task.is_unplanned ? '#f8fafc' : '#fef2f2'}
                                  >
                                    {task.is_unplanned ? '✅' : '🚩'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {orderSaved && (
                  <div style={{ padding: '12px 20px', background: '#f0fdf4', borderTop: '1px solid #bbf7d0', fontSize: '13px', color: '#16a34a', fontWeight: '600' }}>
                    ✅ Queue order saved successfully.
                  </div>
                )}
              </div>}

              {/* Schedule tab */}
              {activeTab === 'schedule' && (
                <div className="card" style={{ marginBottom: '24px', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                  {schedule.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                      No scheduled tasks to display. Add tasks with dates to see the schedule.
                    </div>
                  ) : (
                    <div style={{ padding: '16px' }}>

                      {/* Task legend */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px', padding: '12px', background: '#f8fafc', borderRadius: '10px' }}>
                        {queue.filter(t => t.status_id !== 5).map((t, i) => (
                          <div key={t.task_id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: TASK_COLORS[i % TASK_COLORS.length], flexShrink: 0 }} />
                            <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>{t.task_name}</span>
                          </div>
                        ))}
                      </div>

                      {/* Week rows */}
                      {schedule.map(week => {
                        const isExpanded = expandedWeeks.has(week.weekStart);
                        const hasToday   = week.days.some(d => d.isToday);
                        const weekStartDate = new Date(week.weekStart + 'T12:00:00');
                        const weekEndDate   = new Date(addDay(week.weekStart, 4) + 'T12:00:00');
                        const startYear     = weekStartDate.getFullYear();
                        const endYear       = weekEndDate.getFullYear();
                        const currentYear   = new Date().getFullYear();
                        // Show year on end label always; show on start label only when it differs from end
                        // (handles the "30 Dec – 3 Jan 2026" cross-year case cleanly)
                        const weekLabel     = weekStartDate.toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short',
                          ...(startYear !== endYear ? { year: 'numeric' } : {}),
                        });
                        const weekEndLabel  = weekEndDate.toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        });

                        // Week-level summary: unique tasks across all days
                        const weekTaskMap = {};
                        week.days.forEach(day => {
                          day.tasks.forEach(t => {
                            if (!weekTaskMap[t.task_id]) weekTaskMap[t.task_id] = { ...t, days: 0 };
                            weekTaskMap[t.task_id].days++;
                          });
                        });
                        const weekTasks = Object.values(weekTaskMap);
                        const maxDailyLoad = Math.max(...week.days.map(d => d.totalAlloc), 0);

                        return (
                          <div key={week.weekStart} style={{
                            marginBottom: '8px', border: `1px solid ${hasToday ? '#bfdbfe' : '#e2e8f0'}`,
                            borderRadius: '10px', overflow: 'hidden',
                            boxShadow: hasToday ? '0 0 0 2px #bfdbfe' : 'none',
                          }}>

                            {/* Week header row — clickable to expand */}
                            <div
                              onClick={() => toggleWeek(week.weekStart)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                padding: '10px 14px', cursor: 'pointer',
                                background: hasToday ? '#eff6ff' : '#f8fafc',
                                userSelect: 'none',
                              }}
                            >
                              {/* Chevron */}
                              <span style={{ fontSize: '11px', color: '#94a3b8', transition: 'transform 0.2s', display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>

                              {/* Date range */}
                              <div style={{ minWidth: '140px' }}>
                                <span style={{ fontWeight: '700', fontSize: '13px', color: hasToday ? '#1d4ed8' : '#1e293b' }}>
                                  {weekLabel} – {weekEndLabel}
                                </span>
                                {hasToday && <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: '700', color: '#2563eb', background: '#dbeafe', borderRadius: '4px', padding: '1px 6px' }}>THIS WEEK</span>}
                              </div>

                              {/* Mini stacked bar showing task share across the week */}
                              <div style={{ flex: 1, height: '18px', background: '#e2e8f0', borderRadius: '6px', overflow: 'hidden', display: 'flex' }}>
                                {weekTasks.map((t, i) => (
                                  <div key={t.task_id} title={`${t.task_name}: ${t.alloc}%`} style={{
                                    height: '100%',
                                    width: `${Math.min((t.alloc / Math.max(maxDailyLoad, 100)) * 100, 100 / weekTasks.length)}%`,
                                    background: t.color,
                                    minWidth: weekTasks.length > 0 ? `${100 / weekTasks.length}%` : 'auto',
                                    opacity: 0.85,
                                  }} />
                                ))}
                              </div>

                              {/* Load % badge */}
                              <span style={{
                                fontSize: '11px', fontWeight: '700', minWidth: '48px', textAlign: 'right',
                                color: maxDailyLoad > 100 ? '#ef4444' : maxDailyLoad >= 80 ? '#f59e0b' : '#16a34a',
                              }}>{maxDailyLoad}%</span>

                              {/* Task count */}
                              <span style={{ fontSize: '11px', color: '#94a3b8', minWidth: '60px', textAlign: 'right' }}>
                                {weekTasks.length} task{weekTasks.length !== 1 ? 's' : ''}
                              </span>
                            </div>

                            {/* Expanded: task × weekday grid */}
                            {isExpanded && (() => {
                              // Tasks shown as rows: union of all tasks active on any day this week,
                              // ordered by the queue so colours stay consistent with the legend.
                              const rowTaskIds = [];
                              week.days.forEach(d => gridTasksOnDay(d.date).forEach(t => {
                                if (!rowTaskIds.includes(t.task_id)) rowTaskIds.push(t.task_id);
                              }));
                              const rowTasks = queue
                                .filter(t => rowTaskIds.includes(t.task_id))
                                .map((t, i) => ({ ...t, color: colorMap[t.task_id] }));

                              if (rowTasks.length === 0) {
                                return (
                                  <div style={{ padding: '20px 14px', borderTop: '1px solid #e2e8f0', fontSize: '13px', color: '#94a3b8' }}>
                                    No active tasks this week.
                                  </div>
                                );
                              }

                              // Which tasks are actually active on a given day (for disabling cells)
                              const activeByDay = {};
                              week.days.forEach(d => {
                                activeByDay[d.date] = new Set(gridTasksOnDay(d.date).map(t => t.task_id));
                              });
                              const dayTotal = (dateStr) => rowTasks.reduce((s, t) =>
                                s + (activeByDay[dateStr].has(t.task_id) ? gridCellValue(dateStr, t) : 0), 0);
                              const isSavingThis = savingWeek === week.weekStart;
                              const isSavedThis  = savedWeek === week.weekStart;

                              return (
                                <div style={{ padding: '12px 14px 16px', borderTop: '1px solid #e2e8f0', overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '620px' }}>
                                    <thead>
                                      <tr style={{ background: '#f8fafc' }}>
                                        <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: '600', color: '#475569', minWidth: '180px', position: 'sticky', left: 0, background: '#f8fafc' }}>Task</th>
                                        {week.days.map(day => {
                                          const lbl = new Date(day.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short' });
                                          const dnum = new Date(day.date + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                                          return (
                                            <th key={day.date} style={{ padding: '8px 4px', fontWeight: '600', textAlign: 'center', minWidth: '82px', color: day.isToday ? '#2563eb' : '#475569' }}>
                                              <div>{lbl}</div>
                                              <div style={{ fontSize: '11px', fontWeight: '500', color: day.isToday ? '#2563eb' : '#94a3b8' }}>{dnum}</div>
                                              <button
                                                onClick={() => copyDayAcrossWeek(week, day.date)}
                                                title="Copy this day's values to all days this week"
                                                style={{ marginTop: '3px', fontSize: '9px', color: '#64748b', background: '#eef2f7', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '1px 5px', cursor: 'pointer', fontWeight: '600' }}
                                              >copy →</button>
                                            </th>
                                          );
                                        })}
                                        <th style={{ padding: '8px 8px', fontWeight: '600', textAlign: 'right', minWidth: '70px', color: '#475569' }}>Avg</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {rowTasks.map(task => {
                                        const activeDays = week.days.filter(d => activeByDay[d.date].has(task.task_id));
                                        const rowSum = activeDays.reduce((s, d) => s + gridCellValue(d.date, task), 0);
                                        const rowAvg = activeDays.length ? Math.round(rowSum / activeDays.length) : 0;
                                        return (
                                          <tr key={task.task_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '8px 10px', position: 'sticky', left: 0, background: '#fff' }}>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ width: '4px', height: '26px', borderRadius: '2px', background: task.color, flexShrink: 0 }} />
                                                <div style={{ minWidth: 0 }}>
                                                  <div style={{ fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>{task.task_name}</div>
                                                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{task.project_name} · default {task.allocation_percent || 0}%</div>
                                                </div>
                                              </div>
                                            </td>
                                            {week.days.map(day => {
                                              const active = activeByDay[day.date].has(task.task_id);
                                              if (!active) {
                                                return <td key={day.date} style={{ textAlign: 'center', padding: '6px 4px', color: '#e2e8f0' }}>–</td>;
                                              }
                                              const v = gridCellValue(day.date, task);
                                              // Per-row shading: tint by the task's own colour at low opacity,
                                              // intensifying with the value. No per-day-total tint on cells.
                                              const intensity = v <= 0 ? 0 : Math.min(0.18, 0.04 + (v / 100) * 0.14);
                                              return (
                                                <td key={day.date} style={{ padding: '5px 4px', textAlign: 'center' }}>
                                                  <input
                                                    type="number" min="0" max="100" step="5"
                                                    value={v > 0 ? v : ''}
                                                    placeholder="–"
                                                    onChange={e => setGridCell(day.date, task.task_id, e.target.value)}
                                                    style={{
                                                      width: '58px', height: '32px', textAlign: 'center',
                                                      fontWeight: '700', fontSize: '13px',
                                                      border: `1px solid ${v > 0 ? task.color : '#cbd5e1'}`,
                                                      borderRadius: '6px', outline: 'none',
                                                      background: v > 0 ? hexA(task.color, intensity) : '#fff',
                                                      color: v > 0 ? '#1e293b' : '#94a3b8',
                                                    }}
                                                  />
                                                </td>
                                              );
                                            })}
                                            <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap' }}>{rowAvg}%</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot>
                                      <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                                        <td style={{ padding: '8px 10px', fontWeight: '700', color: '#475569', position: 'sticky', left: 0, background: '#f8fafc' }}>Day total</td>
                                        {week.days.map(day => {
                                          const tot = dayTotal(day.date);
                                          const c = tot > 100 ? '#ef4444' : tot >= 80 ? '#f59e0b' : '#16a34a';
                                          return (
                                            <td key={day.date} style={{ padding: '8px 4px', textAlign: 'center', fontWeight: '800', color: c }}>
                                              {tot}%{tot > 100 && ' ⚠'}
                                            </td>
                                          );
                                        })}
                                        <td />
                                      </tr>
                                    </tfoot>
                                  </table>

                                  {/* Save bar */}
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px', gap: '12px', flexWrap: 'wrap' }}>
                                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                                      Empty cell = not working that task that day. Cells outside a task's date range show “–”.
                                    </div>
                                    <button
                                      onClick={() => saveScheduleWeek(week)}
                                      disabled={isSavingThis}
                                      style={{
                                        padding: '8px 20px', borderRadius: '8px', border: 'none',
                                        background: isSavedThis ? '#16a34a' : '#2563eb',
                                        color: '#fff', fontSize: '13px', fontWeight: '700',
                                        cursor: isSavingThis ? 'default' : 'pointer', whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {isSavedThis ? '✓ Week saved!' : isSavingThis ? 'Saving…' : '💾 Save week'}
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Schedule Day Popover */}
      {schedulePopover && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1100,
        }} onClick={() => setSchedulePopover(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '14px',
            width: '400px', maxWidth: '95vw',
            maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            {/* Header — sticky, never scrolls away */}
            <div style={{ padding: '24px 24px 0', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>
                    Schedule — {new Date(schedulePopover.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>Set how much time to spend on each task</div>
                </div>
                <button onClick={() => setSchedulePopover(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8', padding: '0 4px' }}>✕</button>
              </div>
            </div>

            {/* Task inputs — scrollable */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '0 24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingBottom: '16px' }}>
              {schedulePopover.tasks.map(t => {
                const val = schedulePopover.inputs[t.task_id] ?? t.alloc;
                return (
                  <div key={t.task_id} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '10px 12px', borderRadius: '8px',
                    border: `1px solid #e2e8f0`, borderLeft: `4px solid ${t.color}`,
                    background: '#f8fafc',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.task_name}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{t.project} · default {t.defaultAlloc}%</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      <input
                        type="number" min="0" max="100" step="5"
                        value={val}
                        onChange={e => setSchedulePopover(p => ({
                          ...p,
                          inputs: { ...p.inputs, [t.task_id]: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) },
                        }))}
                        style={{
                          width: '60px', padding: '5px 8px', borderRadius: '6px',
                          border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '700',
                          textAlign: 'center', outline: 'none',
                          color: val > 0 ? '#1e293b' : '#94a3b8',
                        }}
                      />
                      <span style={{ fontSize: '12px', color: '#64748b' }}>%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            </div>{/* end scroll area */}

            {/* Footer: total + actions — always visible, never scrolls */}
            <div style={{ padding: '12px 24px 24px', flexShrink: 0, borderTop: '1px solid #f1f5f9' }}>
            {/* Total indicator */}
            {(() => {
              const total = Object.values(schedulePopover.inputs).reduce((s, v) => s + (parseFloat(v) || 0), 0);
              const color = total > 100 ? '#ef4444' : total === 100 ? '#16a34a' : '#f59e0b';
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: '8px', background: '#f1f5f9', marginBottom: '16px' }}>
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Total allocation</span>
                  <span style={{ fontSize: '15px', fontWeight: '800', color }}>
                    {total}%
                    {total > 100 && ' ⚠ over'}
                    {total === 100 && ' ✓'}
                  </span>
                </div>
              );
            })()}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setSchedulePopover(null)} style={{
                padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
                background: '#f8fafc', color: '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={saveScheduleDay} disabled={schedulePopover.saving || schedulePopover.saved} style={{
                padding: '8px 20px', borderRadius: '8px', border: 'none',
                background: schedulePopover.saved ? '#16a34a' : '#2563eb',
                color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
              }}>
                {schedulePopover.saved ? '✓ Saved!' : schedulePopover.saving ? 'Saving…' : 'Save'}
              </button>
            </div>
            </div>{/* end footer */}
          </div>
        </div>
      )}

      {/* Hold / Resume effective-date modal */}
      {holdModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
        }} onClick={() => setHoldModal(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '14px', width: '420px', maxWidth: '95vw',
            padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b', marginBottom: '4px' }}>
              {holdModal.goingOnHold ? '⏸ Put task on hold' : '▶ Resume task'}
            </div>
            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
              {holdModal.task.task_name}
            </div>
            <div style={{ fontSize: '13px', color: '#475569', marginBottom: '8px', fontWeight: '600' }}>
              {holdModal.goingOnHold ? 'On hold from' : 'Active again from'}
            </div>
            <input
              type="date"
              value={holdModal.date}
              onChange={e => setHoldModal(m => ({ ...m, date: e.target.value }))}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', marginBottom: '6px',
              }}
            />
            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '20px' }}>
              {holdModal.goingOnHold
                ? 'Allocation is removed from this date forward. Past days keep their saved or planned allocation.'
                : 'Allocation resumes from this date. Held days before it stay at 0%.'}
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setHoldModal(null)} style={{
                padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
                background: '#f8fafc', color: '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={confirmHold} disabled={!holdModal.date} style={{
                padding: '8px 20px', borderRadius: '8px', border: 'none',
                background: holdModal.goingOnHold ? '#d97706' : '#16a34a',
                color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
              }}>
                {holdModal.goingOnHold ? 'Put on hold' : 'Resume'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3 className="modal-title">➕ Add Task to Queue</h3>
              <button className="modal-close" onClick={() => setShowTaskModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateTask}>
              <div className="modal-body">
                {taskFormError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 14px', color: '#dc2626', fontSize: '13px', marginBottom: '16px' }}>
                    {taskFormError}
                  </div>
                )}

                <div className="grid grid-2">
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Task Name *</label>
                    <input className="form-control" value={taskForm.task_name}
                      onChange={e => setTaskForm(p => ({ ...p, task_name: e.target.value }))} placeholder="e.g. Fix auth bug" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Project *</label>
                    <select className="form-control" value={taskForm.project_id}
                      onChange={e => setTaskForm(p => ({ ...p, project_id: e.target.value }))}>
                      <option value="">— Select project —</option>
                      {projects.map(p => (
                        <option key={p.project_id} value={p.project_id}>{p.project_name}{p.customer_name ? ` (${p.customer_name})` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Priority</label>
                    <select className="form-control" value={taskForm.priority}
                      onChange={e => setTaskForm(p => ({ ...p, priority: e.target.value }))}>
                      <option>Critical</option><option>High</option><option>Medium</option><option>Low</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Allocation %</label>
                    <input type="number" className="form-control" value={taskForm.allocation_percent} min="1" max="100"
                      onChange={e => setTaskForm(p => ({ ...p, allocation_percent: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Planned Start</label>
                    <input type="date" className="form-control" value={taskForm.planned_start}
                      onChange={e => setTaskForm(p => ({ ...p, planned_start: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Planned End</label>
                    <input type="date" className="form-control" value={taskForm.planned_end}
                      onChange={e => setTaskForm(p => ({ ...p, planned_end: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={taskForm.status_id}
                      onChange={e => setTaskForm(p => ({ ...p, status_id: e.target.value }))}>
                      <option value="6">Not Started</option>
                      <option value="2">In Progress</option>
                      <option value="3">On Hold</option>
                    </select>
                  </div>
                </div>

                {/* Capacity check */}
                <AllocCheckPanel check={allocCheck} checking={allocChecking} />

                {/* Unplanned section */}
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '14px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: taskForm.is_unplanned ? '10px' : '0' }}>
                    <input type="checkbox" checked={taskForm.is_unplanned}
                      onChange={e => setTaskForm(p => ({ ...p, is_unplanned: e.target.checked }))}
                      style={{ width: '16px', height: '16px' }} />
                    <span style={{ fontWeight: '700', color: '#dc2626', fontSize: '13px' }}>🚨 Mark as Unplanned Task</span>
                  </label>
                  {taskForm.is_unplanned && (
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Reason / Context</label>
                      <input className="form-control" value={taskForm.unplanned_reason}
                        onChange={e => setTaskForm(p => ({ ...p, unplanned_reason: e.target.value }))}
                        placeholder="e.g. Production hotfix requested by Acme Corp" />
                    </div>
                  )}
                </div>

                <div className="form-group" style={{ marginTop: '12px' }}>
                  <label className="form-label">Description</label>
                  <textarea className="form-control" rows={2} value={taskForm.description}
                    onChange={e => setTaskForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Optional details…" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTaskModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={taskSaving}>
                  {taskSaving ? 'Creating…' : 'Create & Add to Queue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ── Edit Panel ─────────────────────────────────────────────────────── */}
      {editPanel && (
        <>
          {/* Backdrop */}
          <div
            onClick={closeEditPanel}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.3)',
              zIndex: 1000, backdropFilter: 'blur(2px)',
            }}
          />
          {/* Panel */}
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: '400px',
            background: '#fff', zIndex: 1001, boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
            display: 'flex', flexDirection: 'column',
            animation: 'slideInRight 0.2s ease-out',
          }}>
            <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

            {/* Panel header */}
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            }}>
              <div>
                <div style={{ fontWeight: '800', fontSize: '16px', color: '#1e293b', marginBottom: '4px' }}>
                  ✏️ Edit Task
                </div>
                <div style={{ fontSize: '13px', color: '#64748b', maxWidth: '300px' }}>
                  {editPanel.task_name}
                </div>
                <Link to={`/projects/${editPanel.project_id}`} onClick={closeEditPanel}
                  style={{ fontSize: '12px', color: '#3b82f6', textDecoration: 'none', marginTop: '2px', display: 'block' }}>
                  📁 {editPanel.project_name}
                </Link>
              </div>
              <button onClick={closeEditPanel} style={{
                background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer',
                color: '#94a3b8', lineHeight: 1, padding: '0 4px',
              }}>×</button>
            </div>

            {/* Panel body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>

              {/* Status + Priority */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Status</label>
                  <select
                    value={editForm.status_id}
                    onChange={e => setEditForm(p => ({ ...p, status_id: e.target.value }))}
                    className="form-control"
                  >
                    <option value="6">Not Started</option>
                    <option value="2">In Progress</option>
                    <option value="3">On Hold</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Priority</label>
                  <select
                    value={editForm.priority}
                    onChange={e => setEditForm(p => ({ ...p, priority: e.target.value }))}
                    className="form-control"
                  >
                    <option>Critical</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>
              </div>

              {/* Allocation + Progress */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    Allocation %
                  </label>
                  <input
                    type="number" min="1" max="100"
                    value={editForm.allocation_percent}
                    onChange={e => setEditForm(p => ({ ...p, allocation_percent: e.target.value }))}
                    className="form-control"
                  />
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                    How much of this person's capacity
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    Progress %
                  </label>
                  <input
                    type="number" min="0" max="100"
                    value={editForm.progress}
                    onChange={e => setEditForm(p => ({ ...p, progress: e.target.value }))}
                    className="form-control"
                  />
                </div>
              </div>

              {/* Capacity check */}
              <AllocCheckPanel check={allocCheck} checking={allocChecking} />

              {/* Divider */}
              <div style={{ borderTop: '1px solid #f1f5f9', margin: '4px 0 20px', paddingTop: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '14px' }}>
                  Actual Dates
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                      Actual Start
                    </label>
                    <input
                      type="date"
                      value={editForm.actual_start}
                      onChange={e => setEditForm(p => ({ ...p, actual_start: e.target.value }))}
                      className="form-control"
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                      Actual End
                    </label>
                    <input
                      type="date"
                      value={editForm.actual_end}
                      onChange={e => setEditForm(p => ({ ...p, actual_end: e.target.value }))}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

              {/* Planned dates (read-only reference) */}
              <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Planned Dates (read-only)
                </div>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  {fmt(editPanel.planned_start)} → {fmt(editPanel.planned_end)}
                </div>
              </div>

              {/* Forecast Start */}
              <div style={{ marginTop: '16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '14px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#166534', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  🚀 Forecast Start
                </label>
                <input
                  type="date"
                  value={editForm.forecast_start}
                  onChange={e => setEditForm(p => ({ ...p, forecast_start: e.target.value }))}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #86efac', borderRadius: '6px', fontSize: '14px', background: '#fff', boxSizing: 'border-box' }}
                />
                {editForm.forecast_start && editPanel.planned_start && (
                  <div style={{ fontSize: '11px', color: '#166534', marginTop: '6px' }}>
                    Planned start {fmt(editPanel.planned_start)} · {editForm.forecast_start < editPanel.planned_start
                      ? `starting ${Math.round((new Date(editPanel.planned_start + 'T12:00:00') - new Date(editForm.forecast_start + 'T12:00:00')) / 86400000)} day(s) early`
                      : `starting ${Math.round((new Date(editForm.forecast_start + 'T12:00:00') - new Date(editPanel.planned_start + 'T12:00:00')) / 86400000)} day(s) late`}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: '#166534', marginTop: '4px', opacity: 0.8 }}>
                  Sets expected start without changing the locked planned start
                </div>
              </div>

              {/* Forecast End */}
              <div style={{ marginTop: '12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '14px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  🕐 Forecast End
                </label>
                <input
                  type="date"
                  value={editForm.forecast_end}
                  onChange={e => setEditForm(p => ({ ...p, forecast_end: e.target.value }))}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #f59e0b', borderRadius: '6px', fontSize: '14px', background: '#fff', boxSizing: 'border-box' }}
                />
                {editForm.forecast_end && editPanel.planned_end && (
                  <div style={{ fontSize: '11px', color: '#78350f', marginTop: '6px' }}>
                    Planned end {fmt(editPanel.planned_end)} · {editForm.forecast_end > editPanel.planned_end
                      ? `slipping by ${Math.round((new Date(editForm.forecast_end + 'T12:00:00') - new Date(editPanel.planned_end + 'T12:00:00')) / 86400000)} day(s)`
                      : `${Math.round((new Date(editPanel.planned_end + 'T12:00:00') - new Date(editForm.forecast_end + 'T12:00:00')) / 86400000)} day(s) early`}
                  </div>
                )}
                <div style={{ fontSize: '11px', color: '#92400e', marginTop: '4px' }}>
                  Sets expected completion without changing the original planned end
                </div>
              </div>

            </div>

            {/* Panel footer */}
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #e2e8f0',
              display: 'flex', gap: '10px', justifyContent: 'flex-end',
            }}>
              <button onClick={closeEditPanel} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={editSaving}
                className="btn btn-primary"
                style={{ minWidth: '100px' }}
              >
                {editSaved ? '✅ Saved!' : editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DynamicAllocation;
